import React from "react";
import { ArrowUpRight } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * Changelog section — adapted to the Furloop landing design language.
 *
 * Entry shape:
 *   {
 *     version: string,            // e.g. "v1.2.0"
 *     date?: string,              // e.g. "25 May 2026"
 *     title: string,
 *     description?: string,
 *     items?: string[],           // bullet list of changes
 *     image?: string,             // optional screenshot/illustration URL
 *     button?: { url: string, text: string }
 *   }
 *
 * Content is supplied by the page (see src/components/ChangeLog.js) so it's
 * easy to add new releases over time.
 */

const VersionBadge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-transparent bg-brand-wash px-3 py-1 text-xs font-semibold tracking-wide text-brand-plum">
    {children}
  </span>
);

const Changelog = ({
  eyebrow = "Changelog",
  title = "What's new at Furloop",
  description = "Product updates, improvements, and fixes across the Furloop platform.",
  entries = [],
  className,
}) => {
  return (
    <section
      className={cn("relative min-h-screen bg-brand-bg font-body", className)}
    >
      {/* soft brand glow at the top, consistent with the landing sections */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-brand-wash/70 to-transparent" />

      <div className="relative mx-auto max-w-5xl px-6 py-20 lg:py-28">
        {/* intro */}
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            {eyebrow}
          </span>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ink sm:text-lg">
            {description}
          </p>
        </div>

        {/* entries */}
        {entries.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-line bg-white px-8 py-16 text-center">
            <p className="font-display text-lg font-semibold text-brand-plum">
              No updates yet
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted">
              We're shipping fast — the first release notes will appear here
              soon.
            </p>
          </div>
        ) : (
          <div className="mt-16 space-y-14 md:mt-20 md:space-y-20">
            {entries.map((entry, index) => (
              <article
                key={entry.version ?? index}
                className="relative flex flex-col gap-5 md:flex-row md:gap-12"
              >
                {/* meta column (sticky on desktop) */}
                <div className="flex h-min shrink-0 items-center gap-3 md:sticky md:top-28 md:w-52">
                  <VersionBadge>{entry.version}</VersionBadge>
                  {entry.date && (
                    <time className="text-xs font-medium text-muted">
                      {entry.date}
                    </time>
                  )}
                </div>

                {/* content column with a timeline rail on desktop */}
                <div className="relative flex flex-col md:border-l md:border-line md:pl-10">
                  <span className="absolute -left-[7px] top-1.5 hidden h-3.5 w-3.5 rounded-full bg-cta-gradient ring-4 ring-brand-bg md:block" />

                  {entry.title && (
                    <h2 className="font-display text-xl font-semibold leading-tight text-brand-plum md:text-2xl">
                      {entry.title}
                    </h2>
                  )}
                  {entry.description && (
                    <p className="mt-3 text-sm leading-relaxed text-ink md:text-base">
                      {entry.description}
                    </p>
                  )}

                  {entry.items && entry.items.length > 0 && (
                    <ul className="mt-4 list-none space-y-2.5 pl-0">
                      {entry.items.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          className="mb-0 flex items-start gap-2.5 text-sm leading-relaxed text-muted md:text-base"
                        >
                          <span className="mt-[0.5rem] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-magenta" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {entry.image && (
                    <img
                      src={entry.image}
                      alt={`${entry.title ?? entry.version} preview`}
                      className="mt-6 w-full rounded-xl border border-line object-cover"
                      loading="lazy"
                    />
                  )}

                  {entry.button && (
                    <a
                      href={entry.button.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1 self-start text-sm font-semibold text-brand-magenta no-underline transition-colors hover:text-brand-plum hover:no-underline"
                    >
                      {entry.button.text}
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Changelog;
